import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useCompany } from '../Company/CompanyContext';
import EosLogo from '../layout/EosLogo';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Glass effect states
  const [isVisible, setIsVisible] = useState(false);
  const [blurApplied, setBlurApplied] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const modalRef = useRef(null);
  
  // Common text shadow style with subtle taper and lower opacity
  const textShadowStyle = { textShadow: '0 1px 1px rgba(0,0,0,0.2), 0 2px 3px rgba(0,0,0,0.1)' };
  // More pronounced shadow for headings
  const headingShadowStyle = { textShadow: '0 1px 2px rgba(0,0,0,0.25), 0 2px 4px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.1)' };
  
  const { signup } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();

  // Handle blur and animations on component mount
  useEffect(() => {
    // First render the component
    let blurTimer, backdropTimer, visibilityTimer;
    
    // Apply blur immediately but keep opacity at 0
    blurTimer = setTimeout(() => {
      setBlurApplied(true);
      
      // Wait for blur to be fully applied before showing backdrop
      backdropTimer = setTimeout(() => {
        setBackdropVisible(true);
        
        // Only after backdrop and blur are ready, fade in content
        visibilityTimer = setTimeout(() => {
          setIsVisible(true);
        }, 100);
      }, 50);
    }, 10);
    
    // Cleanup all timers on unmount
    return () => {
      clearTimeout(blurTimer);
      clearTimeout(backdropTimer);
      clearTimeout(visibilityTimer);
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (password !== passwordConfirm) {
      return setError('Passwords do not match');
    }
    
    if (password.length < 6) {
      return setError('Password should be at least 6 characters');
    }
    
    try {
      setError('');
      setLoading(true);
      await signup(email, password);
      navigate('/company-config');
    } catch (err) {
      setError('Failed to create account: ' + err.message);
    }
    
    setLoading(false);
  }

  return (
    <div className="max-w-md w-full px-4 sm:px-0 relative z-10">
      <div 
        ref={modalRef}
        className={`bg-white bg-opacity-40 border border-white border-opacity-30
          rounded-2xl transition-all duration-500 ease-out transform
          ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}
        style={{
          boxShadow: blurApplied 
            ? '0 0 50px 20px rgba(255,255,255,0.2), 0 6px 20px rgba(0,0,0,0.1), 0 10px 40px 25px rgba(0,0,0,0.15)' 
            : 'none',
          backdropFilter: 'blur(14px)',
          background: 'linear-gradient(135deg, rgba(200,200,200,0.35) 0%, rgba(180,180,180,0.25) 100%)'
        }}
      >
        <div className="p-4 sm:p-8">
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="transition-transform duration-300 transform hover:scale-110 w-36 sm:w-44 h-18 sm:h-24 flex items-center justify-center p-1 bg-white bg-opacity-20 rounded-lg shadow-sm">
              <EosLogo customLogoUrl={companyInfo?.customLogoUrl} />
            </div>
          </div>
          
          <h2 className="text-center text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8 font-heading" style={headingShadowStyle}>
            Create your EOS AI account
          </h2>
          
          {error && (
            <div className="bg-red-50 bg-opacity-70 border-l-4 border-red-500 text-red-700 p-3 mb-6 rounded-r-md shadow-sm animate-fade-in" role="alert">
              <p className="font-bold font-heading">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="email" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-gray-400 text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="password" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-gray-400 text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="password-confirm" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Confirm Password
              </label>
              <input
                id="password-confirm"
                name="password-confirm"
                type="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-gray-400 text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg 
                  shadow-eos-orange text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500
                  transition-all duration-300 btn-pop"
                style={textShadowStyle}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </div>
                ) : 'Sign Up'}
              </button>
            </div>
          </form>
          
          <div className="mt-6 sm:mt-8 pt-4 border-t border-white border-opacity-20 text-center">
            <p className="text-sm text-white text-opacity-90" style={textShadowStyle}>
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-medium text-white hover:text-gray-500 transition-all duration-300"
                style={textShadowStyle}
              >
                Log in
              </button>
            </p>
            
            <p className="text-xs text-white text-opacity-70 mt-4 animate-pulse-slow" style={textShadowStyle}>
              Powered by the Entrepreneurial Operating System®
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;